import { useEffect, useRef, useState } from 'react';
import unsubscribleLogo from '../../images/vizio-logo-print.svg';
import useTechnicalErrorMessage from '../common/hooks/useTechnicalErrorMessage';
import * as Sentry from '@sentry/react';
import { authAPI } from '../../services/redwolfSdkAPI';
import Spinner from '../common/components/Spinner';
import useRequestRedwolfService from '../common/hooks/useRequestRedwolfService';
import { useRecaptcha } from '../common/hooks/useRecaptcha';
import { useLocation } from 'react-router-dom';
import { checkEmailFormat } from '../../utils/validations';
import { addClasses } from '../../utils/eventListeners';

interface UnsubscriptionPayload {
	reCaptchaToken: string;
	email: string;
}

const EmailUnsubscribe = () => {
	const [isUnsubscribeSucceed, setIsUnsubscribeSucceed] = useState(false);
	const [showTechnicalErrorMessage] = useTechnicalErrorMessage();
	const [isSpinnerLoading, setIsSpinnerLoading] = useState(false);
	const [requestRedwolfService] = useRequestRedwolfService();
	const [isRecaptchaLoading, generateRecaptchaToken]: any = useRecaptcha();
	const params: any = new URLSearchParams(useLocation().search);

	const [emailAddress, setEmailAddress] = useState('');
	const [errorMessage, setErrorMessage] = useState('');
	const emailRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		document.title = 'Unsubscribe | VIZIO';
		addClasses();

		const emailValue = params.get('email') || (params.get('em') && window.atob(params.get('em')));

		if (emailValue) {
			setEmailAddress(emailValue);
		}
	}, []);

	useEffect(() => {
		setIsSpinnerLoading(!isRecaptchaLoading);
	}, [isRecaptchaLoading]);

	const handleInputChange = (event: any) => {
		setEmailAddress(event.target.value);
		setErrorMessage('');
	};

	const handleSubmit = async () => {
		if (!emailAddress) {
			setErrorMessage('This is a required field.');
			emailRef.current && emailRef.current.focus();
		} else if (emailAddress && !checkEmailFormat(emailAddress)) {
			setErrorMessage('Please enter a valid email address.');
			emailRef.current && emailRef.current.focus();
		} else {
			setIsSpinnerLoading(true);
			setIsUnsubscribeSucceed(false);

			const captchaToken = await generateRecaptchaToken('vz_account_email_unsubscribe').then(
				(response: any) => response
			);
			const unSubscriptionPayload: UnsubscriptionPayload = {
				reCaptchaToken: captchaToken,
				email: emailAddress
			};

			requestRedwolfService(authAPI.unsubscribe(unSubscriptionPayload))
				.then((response: any) => {
					setIsSpinnerLoading(false);
					if (response.constructor.$metadata$.simpleName === 'Success') {
						setIsUnsubscribeSucceed(true);
					} else {
						showTechnicalErrorMessage();
					}
				})
				.catch((err: any) => {
					setIsSpinnerLoading(false);
					showTechnicalErrorMessage();
					Sentry.captureException(err);
				});
		}
	};

	if (emailRef.current && emailRef.current.value) {
		emailRef.current.classList.add('active');
	}

	return (
		<div className="unsubscribe-view">
			<div className="unsubscrible">
				<div className="unsubscrible-logo">
					<img src={unsubscribleLogo} alt="logo" />
				</div>
				{isUnsubscribeSucceed ? (
					<div className="success-subscribe">
						<h1 className="h1" tabIndex={-1} aria-live="polite">
							Thank you
						</h1>
						<p>You have successfully unsubscribed from VIZIO mailing list</p>
					</div>
				) : (
					<div className="unsubscribe-info">
						<p>If you wish to unsubscribe from the mailing list, please click below.</p>

						<div className={`field ${errorMessage ? 'has-error' : ''}`}>
							<div className="input-box form-group input-label">
								<input
									id="email"
									name="email"
									type="text"
									className="form-control input-text"
									aria-describedby="emailError"
									aria-required="true"
									value={emailAddress}
									onChange={handleInputChange}
									ref={emailRef}
								/>
								<label htmlFor="email" className="control-label">
									Email address
								</label>

								<code id="emailError">{errorMessage}</code>
							</div>
						</div>

						<button className="primary-button" onClick={handleSubmit}>
							Unsubscribe
						</button>
					</div>
				)}
			</div>
			{isSpinnerLoading && <Spinner />}
		</div>
	);
};

export default EmailUnsubscribe;
